import React, { useState, useEffect, useMemo } from 'react';
import Mail from 'lucide-react/dist/esm/icons/mail.js';
import Phone from 'lucide-react/dist/esm/icons/phone.js';
import MapPin from 'lucide-react/dist/esm/icons/map-pin.js';
import { useTranslations } from '../../../contexts/TranslationContext';
import api, { apiRoutes } from '../../../api';
import { getConfig } from '../../../config/environment';

export default function AboutContactCardSection({ section }) {
  const { t } = useTranslations();
  const [contactInfo, setContactInfo] = useState(null);
  const [contactLoading, setContactLoading] = useState(true);

  // Fetch real contact info from the API
  useEffect(() => {
    let cancelled = false;
    const fetchContact = async () => {
      try {
        const { tlfId } = getConfig();
        const res = await api.get(apiRoutes.OrganizationContactInformation, {
          params: { organizationId: tlfId },
        });
        const data = res.data?.payload || res.data;
        if (!cancelled && data) setContactInfo(data);
      } catch (err) {
        console.error('[AboutContactCardSection] Failed to load contact info:', err);
      } finally {
        if (!cancelled) setContactLoading(false);
      }
    };
    fetchContact();
    return () => { cancelled = true; };
  }, []);

  // Build contact details array from real API data
  const hasContactData = contactInfo && (contactInfo.email || contactInfo.phone || contactInfo.address1);
  const contactDetails = useMemo(() => {
    if (!contactInfo) return [];
    const items = [];
    if (contactInfo.email) {
      items.push({
        icon: Mail,
        label: t('about.email', 'Email'),
        value: contactInfo.email,
        href: `mailto:${contactInfo.email}`,
        colorClass: 'blue',
      });
    }
    if (contactInfo.phone) {
      items.push({
        icon: Phone,
        label: t('about.phone', 'Phone'),
        value: contactInfo.phone,
        href: `tel:${contactInfo.phone}`,
        colorClass: 'green',
      });
    }
    const addressParts = [contactInfo.address1, contactInfo.address2, contactInfo.city, contactInfo.province, contactInfo.postalCode, contactInfo.country].filter(Boolean);
    if (addressParts.length > 0) {
      items.push({
        icon: MapPin,
        label: t('about.address', 'Address'),
        value: addressParts.join(', '),
        href: null,
        colorClass: 'orange',
      });
    }
    return items;
  }, [contactInfo, t]);

  if (contactLoading || !hasContactData) return null;

  return (
    <div className="rounded-2xl p-2 sm:p-6 md:p-12 mb-6 sm:mb-12">
      <h2 className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-8 text-center">
        {t('about.contactUs', 'Contact Us')}
      </h2>

      <div className={`grid gap-3.5 sm:gap-6 ${contactDetails.length >= 3 ? 'grid-cols-1 md:grid-cols-3' : contactDetails.length === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 max-w-md mx-auto'}`}>
        {contactDetails.map((item, index) => {
          const Icon = item.icon;
          const colorMap = {
            blue: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400' },
            green: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-600 dark:text-green-400' },
            orange: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-600 dark:text-orange-400' },
          };
          const colors = colorMap[item.colorClass] || colorMap.blue;
          return (
            <div key={index} className="bg-white dark:bg-gray-700 rounded-xl p-3.5 sm:p-6 shadow-md border border-gray-200 dark:border-gray-600">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 ${colors.bg} rounded-lg flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${colors.text}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-xs sm:text-base text-gray-900 dark:text-white mb-1 sm:mb-2">{item.label}</h3>
                  {item.href ? (
                    <a href={item.href} className={`${colors.text} text-xs sm:text-sm hover:underline break-all`}>
                      {item.value}
                    </a>
                  ) : (
                    <p className="text-gray-600 dark:text-gray-300 text-xs sm:text-sm break-words">{item.value}</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
